import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router";
import { yupResolver } from "@hookform/resolvers/yup";
import { SmartHealthLinkSchema } from "../../../utils/formUtils";
import { errorLog } from "../../../utils/logger";
import { DocketAPIError, getAPIClient } from "../../../apiClient";
import ResponsiveDatePicker from "../../../components/form/ResponsiveDatePicker";
import { ErrorModal } from "../../../components/modals/ErrorModal";
import { DateTime } from "luxon";
import { updateSmartHealthLinkAtoms, setSmartHealthLinkPasscode } from "../../../jotai/atomUpdater";

export default function AddLink({ recordId }: { recordId: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showErrorModal, setShowErrorModal] = useState<string>();

  const {
    register,
    setValue,
    getValues,
    formState: { errors },
    handleSubmit,
  } = useForm({
    mode: "onBlur",
    resolver: yupResolver(SmartHealthLinkSchema),
    defaultValues: {
      expirationDate: DateTime.now().plus({ days: 1 }).toJSDate(),
      passcode: "",
      izHistoryUid: recordId,
    },
  });

  const onSubmit = async (data: any) => {
    try {
      const newLink = await getAPIClient().postSmartHealthLink({
        izHistoryUid: data.izHistoryUid,
        expirationDate: data.expirationDate,
        passcode: data.passcode,
      });
      await updateSmartHealthLinkAtoms();
      await setSmartHealthLinkPasscode(newLink.id.toString(), data.passcode);

      // Refreshing should render edit view since we store the link in state
      navigate(0);
    } catch (e: unknown) {
      errorLog(e as Error);
      if (e instanceof DocketAPIError) {
        setShowErrorModal(e.message);
      }
    }
  };

  return (
    <div>
      {showErrorModal && (
        <ErrorModal title={t("generic.error")} onCloseModal={() => setShowErrorModal(undefined)}>
          <p>{showErrorModal}</p>
        </ErrorModal>
      )}
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="flex flex-col gap-2">
          <div className="flex flex-col gap-1">
            <p className="text-dark dark:text-white text-3xl max-md:text-2xl mobile-text-color max-md:font-semibold">
              {t("immunizations.shlink_new_title")}
            </p>
            <p className="text-dark dark:text-white text-md mobile-text-color max-md:text-md">
              {t("immunizations.shlink_new_message")}
            </p>
          </div>
          <div>
            <label className="text-dark text-[16px] font-semibold dark:text-white mobile-text-color max-md:mb-0 max-md:text-white">
              Expiration Date
            </label>
            <div className="mobile-text-color">
              <ResponsiveDatePicker
                key="expirationDate"
                selectedDate={getValues("expirationDate") ?? null}
                setSelectedDate={(d) =>
                  setValue("expirationDate", d ?? DateTime.now().plus({ days: 1 }).toJSDate())
                }
                id="expirationDate"
              />
            </div>
            {errors.expirationDate && (
              <p className="text-red-500 text-xs">{errors.expirationDate.message}</p>
            )}
          </div>
          <div className="flex flex-col">
            <p className="text-[16px] font-[700] text-dark dark:text-white mobile-text-color">
              {" "}
              Passcode
            </p>
            <label className="input input-neutral !border-black max-md:w-full max-md:border-0 max-md:!bg-white">
              <svg
                className="h-[1em] text-dark dark:md:text-white"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
              >
                <g
                  strokeLinejoin="round"
                  strokeLinecap="round"
                  strokeWidth="2.5"
                  fill="none"
                  stroke="currentColor"
                >
                  <path d="M2.586 17.414A2 2 0 0 0 2 18.828V21a1 1 0 0 0 1 1h3a1 1 0 0 0 1-1v-1a1 1 0 0 1 1-1h1a1 1 0 0 0 1-1v-1a1 1 0 0 1 1-1h.172a2 2 0 0 0 1.414-.586l.814-.814a6.5 6.5 0 1 0-4-4z"></path>
                  <circle cx="16.5" cy="7.5" r=".5" fill="currentColor"></circle>
                </g>
              </svg>
              <input
                data-testid="passcode"
                className="!border-none text-dark dark:md:text-white text-base max-md:p-8"
                type="password"
                required
                minLength={4}
                maxLength={8}
                {...register("passcode")}
              />
            </label>
            {errors.passcode && (
              <p className="text-red-500 text-xs mt-1">{errors.passcode.message}</p>
            )}
          </div>
          <div className="flex flex-col">
            <p className="text-dark dark:text-white text-xs mobile-text-color">
              {t("immunizations.shlink_new_legal")}
            </p>
            <a
              className="text-dark dark:text-white text-xs underline mobile-text-color"
              target="_blank"
              href={t("immunizations.shlink_url")}
            >
              {t("immunizations.shlink_url")}
            </a>
          </div>
          <button
            data-testid="activate-button"
            className="btn docket-button-responsive md:w-fit max-md:w-full"
            type="submit"
          >
            {t("immunizations.shlink_new_button")}
          </button>
        </div>
      </form>
    </div>
  );
}
